import * as React from 'react';

import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import ProgramArchivedDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramArchivedDenyModal';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useEnterpriseContracts,
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { isWhitelistOrganizationProgramsForSSOLogin } from 'bundles/unified-common/utils/isWhitelistOrganizationProgramsForSSOLogin';

type Props = {
  naptime?: InjectedNaptime;
  children: React.ReactNode;
};

// Checks data required to serve an enterprise page, if said data is not present returns appropriate error page.
export const EnterprisePageGuard = ({ children, naptime }: Props) => {
  const program = useProgram();
  const enterpriseContracts = useEnterpriseContracts();
  const isProgramLoading = useIsProgramLoading();
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrg = useThirdPartyOrganization();
  const isLearnerPartOfLfoEnabledOrg = features?.enableOptimizedLearnerFlow && location.query.isExternal === 'true';
  const shouldRedirectToIdpOnOrg =
    isLearnerPartOfLfoEnabledOrg &&
    (features.enableForceSsoLogin || features.enableForceSsoLoginV2Beta) &&
    isWhitelistOrganizationProgramsForSSOLogin({ thirdPartyOrgId: thirdPartyOrg?.id, programId: program?.id });
  const shouldRedirectToIdp =
    !isAuthenticatedUser() && (location.query.attemptSSOLogin === 'true' || shouldRedirectToIdpOnOrg);
  if (shouldRedirectToIdp && thirdPartyOrg) {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrg.id} />;
  }
  const shouldRedirectToLoginForLfo =
    !isAuthenticatedUser() &&
    features?.enableOptimizedLearnerFlow &&
    location.query.isExternal &&
    !location.query.attemptSSOLogin;
  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));

    return null;
  }

  // Show program archived page if program is archived.
  if (
    !isProgramLoading &&
    program &&
    (program?.metadata.endedAt || (enterpriseContracts && allContractsHardPausedOrTerminated(enterpriseContracts)))
  ) {
    return <ProgramArchivedDenyModal userId={user.get()?.id} naptime={naptime} />;
  }

  // If no program found, render NotFound page.
  if (!isProgramLoading && !program) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageGuard);
